import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal, Input, Button, Select, AutoComplete, Tag } from 'antd'
import TextArea from 'antd/lib/input/TextArea'
import { CloseOutlined } from '@ant-design/icons'
import { http } from '@services/http'
import { useSelector } from 'react-redux'
import SuccessModal from '@components/Modal/SuccessModal'
import ErrorModal from '@components/Modal/ErrorModal'
import { useQuery } from '@tanstack/react-query'
import { get } from 'lodash'
import { debouncedSet } from '@utils/debounce'
import {
	fetchDdsArticle,
	fetchCashAccounts,
	fetchDocRate,
} from '@utils/fetchData'
import moment from 'moment'
import { formatInputNumber, removeSpaces } from '@utils/numberWithSpaces'

const PayEmployeesModal = ({ visible, onClose }) => {
	const { t } = useTranslation()
	const successRef = useRef()
	const errorRef = useRef()

	const roleNames = useSelector((state) => state.main.roleNames)

	const uzsCash = useSelector((state) => state.main.uzsCash)
	const usdCash = useSelector((state) => state.main.usdCash)

	const [loading, setLoading] = useState(false)
	const [dds, setDds] = useState('')
	const [currency, setCurrency] = useState('UZS')
	const [comment, setComment] = useState('')
	const [amount, setAmount] = useState('')
	const [docDate, setDocDate] = useState(moment().format('YYYY-MM-DD'))
	const [clientCode, setClientCode] = useState('')
	const [clientName, setClientName] = useState('')
	const [searchClientName, setSearchClientName] = useState('')
	const [cash, setCash] = useState('')
	const [docRate, setDocRate] = useState('')

	const fetchEmployees = async ({ queryKey }) => {
		const [_, cardName, api] = queryKey
		try {
			let url = `api/businesspartners/employee-suppliers?cardName=${cardName}`
			const { data } = await http.get(url)
			return data
		} catch (error) {
			console.log(error)
		}
	}

	const postData = async () => {
		setLoading(true)
		try {
			await http.post('api/vendorpayments/suppliers', {
				cardCode: clientCode,
				cashAccount: roleNames.includes('Cashier') ? uzsCash : cash,
				docCurrency: currency,
				cashSum: Number(removeSpaces(amount)),
				remarks: comment,
				u_DdsArticle: dds,
				docDate,
				docRate: +removeSpaces(docRate),
			})
			resetForm()
			onClose()

			successRef.current.open(t('sucCreated'))
		} catch (error) {
			errorRef.current.open(error?.response?.data?.message)
		} finally {
			setLoading(false)
		}
	}

	const resetForm = () => {
		setDds('')
		setCurrency('UZS')
		setComment('')
		setAmount('')
		setDocDate(moment().format('YYYY-MM-DD'))
		setClientCode('')
		setClientName('')
		setSearchClientName('')
		setCash('')
		setDocRate('')
	}

	const { data: todayDocRate, isLoadingDocRate } = useQuery({
		queryKey: ['docRate'],
		queryFn: fetchDocRate,
		keepPreviousData: true,
		refetchOnWindowFocus: true,
	})

	useEffect(() => {
		if (todayDocRate) {
			setDocRate(todayDocRate)
		}
	}, [todayDocRate, visible])

	const { data: ddsData = [], isLoading: ddsLoading } = useQuery({
		queryKey: ['dds-articles'],
		queryFn: fetchDdsArticle,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const { data: cashData = [], isLoading: cashLoading } = useQuery({
		queryKey: ['cashAcc'],
		queryFn: fetchCashAccounts,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const {
		data: clientsData = [],
		isLoading: loadingClient,
		refetch,
	} = useQuery({
		queryKey: ['employee-suppliers', searchClientName],
		queryFn: fetchEmployees,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const handleCardNameChange = (val) => {
		debouncedSet(val, setSearchClientName)
	}

	console.log('clientsData', clientsData)

	return (
		<Modal
			title={
				<h1 className="font-nunito  font-extrabold text-[#000000]">
					{t('paySupplier')}
				</h1>
			}
			open={visible}
			onCancel={onClose}
			footer={null}
			closeIcon={<CloseOutlined />}
			width="75%"
			style={{ height: '70%' }}
		>
			<div className="w-full px-2 sm:px-2 overflow-y-auto">
				<div className="flex items-center justify-between mt-5">
					<div className=" flex flex-col items-start gap-3  ">
						<p className="font-bold">{t('employee')}</p>
						<AutoComplete
							loading={loadingClient}
							showSearch
							value={clientName}
							className="flex-1 h-12 w-[350px]"
							onSelect={(value, label) => {
								console.log('label', label)
								const cardName = label?.data.cardName
								const cardCode = label?.data.cardCode
								setClientName(cardName)
								setClientCode(cardCode)
								handleCardNameChange(cardName)
							}}
							onChange={(val) => {
								const value = val.split(' ,/ ')[0]
								setClientName(value)
								handleCardNameChange(value)
							}}
							options={clientsData.map((client) => ({
								value: `${client.cardName},/ ${client.cardCode}`,
								label: (
									<div className="flex items-center justify-between">
										<span>{client.cardName}</span>
									</div>
								),
								data: client,
							}))}
						/>
					</div>

					<div className="flex flex-col gap-3 ">
						<p className="font-bold">{t('currency')}</p>

						<Select
							type="text"
							onChange={(val) => {
								setCurrency(val)
								setCash('')
							}}
							value={currency}
							disabled
							className="flex h-8 w-[100px] items-center justify-center "
						>
							<Select.Option value={'UZS'}>UZS</Select.Option>
						</Select>
					</div>

					<div className="flex flex-col items-start gap-3 ">
						<p className="font-bold">{t('docRate')}</p>
						<Input
							type="text"
							value={formatInputNumber(docRate, 2)}
							className=" h-8 w-auto "
							onChange={(e) => {
								setDocRate(e.target.value)
							}}
							disabled={!roleNames.includes('admin')}
						/>
					</div>
					{!roleNames.includes('Cashier') && (
						<div className="flex flex-col gap-3 ">
							<p className="font-bold">{t('kassa')}</p>
							<Select
								type="text"
								onChange={(val) => {
									setCash(val)
								}}
								value={cash}
								disabled={!currency}
								className="flex h-8 w-[250px] items-center justify-center "
								options={cashData
									.filter(
										(cash) =>
											cash.actCurr === currency ||
											cash.actCurr === '##',
									)
									.map((cash) => ({
										value: cash.acctCode,
										label: `${cash.acctCode} - ${cash.acctName}`,
									}))}
							/>
						</div>
					)}
				</div>
				<div className="flex items-center justify-between mt-5">
					<div className=" flex flex-col items-start gap-3  ">
						<p className="font-bold">{t('dds')}</p>
						<Select
							type="text"
							onChange={(val) => {
								setDds(val)
							}}
							value={dds}
							loading={ddsLoading}
							className="flex h-8 w-[350px] items-center justify-center"
						>
							{ddsData.map((dds, idx) => (
								<Select.Option key={idx} value={dds.fldValue}>
									{dds.descr}
								</Select.Option>
							))}
						</Select>
					</div>
					<div className="flex flex-col items-start gap-3 ">
						<p className="font-bold">{t('payingAmount')}</p>
						<Input
							type="text"
							onChange={(e) => {
								setAmount(removeSpaces(e.target.value))
							}}
							value={formatInputNumber(amount)}
							className="flex h-8 w-[250px] items-center justify-center border-[1px] border-[#D0D0D0]"
						/>
					</div>
				</div>
				<div className="flex flex-col items-start gap-3 w-full mt-5">
					<p className="font-bold">{t('comments')}</p>
					<TextArea
						onChange={(e) => {
							setComment(e.target.value)
						}}
						value={comment}
						className="w-full"
						rows={4}
					/>
				</div>

				<div className="flex items-center gap-10 mt-14 justify-between">
					<Button
						onClick={() => {
							onClose()
						}}
						className="w-full bg-red-600 h-[35px] rounded-lg text-white"
					>
						{t('back')}
					</Button>
					<Button
						onClick={postData}
						loading={loading}
						disabled={loading}
						className="w-full bg-[#0A4D68] h-[35px] rounded-lg text-white"
					>
						{t('add')}
					</Button>
				</div>
			</div>
			<SuccessModal
				getRef={(r) => {
					successRef.current = r
				}}
			/>
			<ErrorModal
				getRef={(r) => {
					errorRef.current = r
				}}
			/>
		</Modal>
	)
}

export default PayEmployeesModal
